import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { createPortal } from 'react-dom';
import { AlertTriangle, ArrowRightLeft, Loader2 } from 'lucide-react';
import { apiHelpers } from '@/lib/api';
import { getErrorMessage } from '@/lib/errors';
import { qk } from '@/lib/queryKeys';
import { cn } from '@/lib/utils';
import { LoadingSpinner } from '@/components/ui/loading-spinner';
import type { UserDeletionImpact } from '@/types';

interface DeleteUserDialogProps {
  userId: string;
  userName: string;
  isPending: boolean;
  /** `transferOwnership` is teamId → new owner userId; empty when nothing is shared. */
  onConfirm: (transferOwnership: Record<string, string>) => void;
  onCancel: () => void;
}

/**
 * Delete-user confirmation with ownership transfer.
 *
 * Deleting an account cascades into the teams it owns — and a team may hold
 * other people's work. Rather than refusing outright, this dialog asks the
 * backend what would be destroyed (GET /users/:id/deletion-impact) and splits
 * the answer in two:
 *
 *   • Teams nobody else works in are listed as what they are: deleted with the
 *     account, projects and all. (Their GitHub repos are archived, never
 *     deleted — platform policy — so the code survives.)
 *   • Teams with other active members get a "hand it to" picker. Confirm stays
 *     disabled until every one of them has a new owner, which is the same rule
 *     the API enforces with 409 OWNERSHIP_TRANSFER_REQUIRED. The dialog exists
 *     so an admin never meets that error blind.
 */
export function DeleteUserDialog({
  userId,
  userName,
  isPending,
  onConfirm,
  onCancel,
}: DeleteUserDialogProps) {
  const [transfers, setTransfers] = useState<Record<string, string>>({});

  const {
    data,
    isPending: isLoadingImpact,
    error,
  } = useQuery({
    queryKey: qk.userDeletionImpact(userId),
    queryFn: () => apiHelpers.getUserDeletionImpact(userId),
  });

  const impact: UserDeletionImpact | undefined = data?.data?.data;
  const sharedTeams = useMemo(() => impact?.sharedTeams ?? [], [impact]);
  const soleOwnerTeams = impact?.soleOwnerTeams ?? [];

  const unresolved = sharedTeams.filter((t) => !transfers[t.teamId]);
  const canConfirm = !isLoadingImpact && !error && unresolved.length === 0;

  const totalProjectsDeleted = soleOwnerTeams.reduce((n, t) => n + t.projectCount, 0);

  if (typeof document === 'undefined') return null;

  return createPortal(
    <>
      <div className="fixed inset-0 z-[10000] bg-black/50" onClick={onCancel} />
      <div className="fixed inset-0 z-[10001] flex items-center justify-center p-4">
        <div
          className="bg-card max-h-[85vh] w-full max-w-lg overflow-y-auto rounded-xl border shadow-lg"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="p-6">
            <div className="mb-4 flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-red-100 dark:bg-red-900/30">
                <AlertTriangle className="h-5 w-5 text-red-600 dark:text-red-400" />
              </div>
              <h2 className="text-lg font-semibold">Delete {userName}</h2>
            </div>

            {isLoadingImpact && (
              <div className="text-muted-foreground flex items-center gap-2 py-6 text-sm">
                <Loader2 className="h-4 w-4 animate-spin" />
                Checking what this account owns…
              </div>
            )}

            {error && (
              <p className="mb-6 rounded-lg bg-red-50 p-3 text-sm text-red-700 dark:bg-red-900/20 dark:text-red-300">
                {getErrorMessage(error, "Could not check this account's teams")}
              </p>
            )}

            {impact && (
              <div className="space-y-5">
                {soleOwnerTeams.length === 0 && sharedTeams.length === 0 && (
                  <p className="text-muted-foreground text-sm">
                    This account owns no teams. Deleting it removes the login and its team
                    memberships. Nothing else is affected.
                  </p>
                )}

                {soleOwnerTeams.length > 0 && (
                  <div>
                    <h3 className="mb-2 text-sm font-medium">Deleted with this account</h3>
                    <ul className="space-y-1 rounded-lg border p-3 text-sm">
                      {soleOwnerTeams.map((t) => (
                        <li key={t.teamId} className="flex items-center justify-between gap-3">
                          <span className="truncate">{t.teamName || t.teamId}</span>
                          <span className="text-muted-foreground shrink-0 text-xs">
                            {t.projectCount} project{t.projectCount === 1 ? '' : 's'}
                          </span>
                        </li>
                      ))}
                    </ul>
                    <p className="text-muted-foreground mt-2 text-xs">
                      {totalProjectsDeleted} project{totalProjectsDeleted === 1 ? '' : 's'} and all
                      their data will be permanently deleted. Cloudflare Workers, R2 files and
                      custom domains are torn down and subscriptions cancelled. GitHub repos are
                      archived, not deleted.
                    </p>
                  </div>
                )}

                {sharedTeams.length > 0 && (
                  <div>
                    <h3 className="mb-1 flex items-center gap-2 text-sm font-medium">
                      <ArrowRightLeft className="h-4 w-4" />
                      Hand over to a new owner
                    </h3>
                    <p className="text-muted-foreground mb-3 text-xs">
                      Other people are active on these teams, so they are never deleted with the
                      account. Choose who takes each one over — their projects go with them.
                    </p>
                    <div className="space-y-3">
                      {sharedTeams.map((t) => (
                        <div key={t.teamId} className="rounded-lg border p-3">
                          <div className="mb-2 flex items-center justify-between gap-3">
                            <span className="truncate text-sm font-medium">
                              {t.teamName || t.teamId}
                            </span>
                            <span className="text-muted-foreground shrink-0 text-xs">
                              {t.projectCount} project{t.projectCount === 1 ? '' : 's'}
                            </span>
                          </div>
                          <select
                            className="bg-background w-full rounded-lg border px-3 py-2 text-sm"
                            value={transfers[t.teamId] ?? ''}
                            onChange={(e) =>
                              setTransfers((prev) => ({ ...prev, [t.teamId]: e.target.value }))
                            }
                          >
                            <option value="">Select a new owner…</option>
                            {t.candidates.map((cand) => (
                              <option key={cand.userId} value={cand.userId}>
                                {cand.name ? `${cand.name} — ` : ''}
                                {cand.email} ({cand.role})
                              </option>
                            ))}
                          </select>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            <div className="mt-6 flex gap-3">
              <button
                onClick={onCancel}
                disabled={isPending}
                className="hover:bg-muted flex-1 rounded-lg border px-4 py-2 text-sm font-medium transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={() => onConfirm(transfers)}
                disabled={!canConfirm || isPending}
                className={cn(
                  'flex flex-1 items-center justify-center rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-red-700',
                  'disabled:cursor-not-allowed disabled:opacity-50'
                )}
                title={
                  unresolved.length > 0
                    ? 'Choose a new owner for every shared team first'
                    : undefined
                }
              >
                {isPending ? <LoadingSpinner size="sm" variant="icon" /> : 'Delete account'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>,
    document.body
  );
}
